import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  MonthlyViewResponse,
  RescheduleTicketInput,
  RescheduledTicket,
  WeeklyViewResponse,
  DailyViewResponse,
} from '@sime/shared';

// ---------------------------------------------------------------------------
// Response wrappers
// ---------------------------------------------------------------------------

interface MonthlyViewApiResponse {
  success: true;
  data: MonthlyViewResponse;
}

interface WeeklyViewApiResponse {
  success: true;
  data: WeeklyViewResponse;
}

interface DailyViewApiResponse {
  success: true;
  data: DailyViewResponse;
}

interface RescheduleApiResponse {
  success: true;
  data: RescheduledTicket;
}

// ---------------------------------------------------------------------------
// Filter params for the monthly view
// ---------------------------------------------------------------------------

export interface MonthlyFilters {
  ambitoId?: number;
  subcontratoId?: number;
  tipoEquipoId?: number;
  catalogoecoId?: number;
}

/** Appends the optional filter params shared by all three calendar endpoints. */
function appendCalendarFilterParams(searchParams: URLSearchParams, filters: MonthlyFilters): void {
  if (filters.ambitoId !== undefined) searchParams.set('ambitoId', String(filters.ambitoId));
  if (filters.subcontratoId !== undefined) searchParams.set('subcontratoId', String(filters.subcontratoId));
  if (filters.tipoEquipoId !== undefined) searchParams.set('tipoEquipoId', String(filters.tipoEquipoId));
  if (filters.catalogoecoId !== undefined) searchParams.set('catalogoecoId', String(filters.catalogoecoId));
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const monthlyKeys = {
  all: ['plan-matriz-monthly'] as const,
  month: (year: number, month: number, filters: MonthlyFilters) =>
    [...monthlyKeys.all, year, month, filters] as const,
  week: (date: string, filters: MonthlyFilters) =>
    [...monthlyKeys.all, 'weekly', date, filters] as const,
  day: (date: string, filters: MonthlyFilters) =>
    [...monthlyKeys.all, 'daily', date, filters] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches tickets grouped by day for the given month.
 * GET /api/v1/plan-matriz/monthly/:year/:month
 */
export function useMonthlyTickets(
  year: number,
  month: number,
  filters: MonthlyFilters = {},
  options?: Omit<UseQueryOptions<MonthlyViewApiResponse>, 'queryKey' | 'queryFn'>,
) {
  const searchParams = new URLSearchParams();
  appendCalendarFilterParams(searchParams, filters);

  const qs = searchParams.toString();
  const endpoint = `/plan-matriz/monthly/${year}/${month}${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: monthlyKeys.month(year, month, filters),
    queryFn: () => apiClient<MonthlyViewApiResponse>(endpoint),
    staleTime: 30_000, // 30s — calendar data is semi-static
    ...options,
  });
}

/**
 * Fetches tickets for a Wed→Tue week containing the given date (anchor).
 * GET /api/v1/plan-matriz/weekly/:date
 */
export function useWeeklyTickets(
  date: string,
  filters: MonthlyFilters = {},
  options?: Omit<UseQueryOptions<WeeklyViewApiResponse>, 'queryKey' | 'queryFn'>,
) {
  const searchParams = new URLSearchParams();
  appendCalendarFilterParams(searchParams, filters);

  const qs = searchParams.toString();
  const endpoint = `/plan-matriz/weekly/${date}${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: monthlyKeys.week(date, filters),
    queryFn: () => apiClient<WeeklyViewApiResponse>(endpoint),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Reschedule a ticket and invalidate ALL calendar caches plus the affected
 * ticket detail / history queries. Used from the weekly/daily views and from
 * the calendar TicketCard quick-action — anywhere a stale ticket-detail query
 * could otherwise show the previous fechaProgramada after a successful reschedule.
 */
export function useRescheduleTicketAcrossViews() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, newDate }: { ticketId: number; newDate: string }) => {
      const body: RescheduleTicketInput = { newDate };
      return apiClient<RescheduleApiResponse>(`/plan-matriz/reschedule/${ticketId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },
    onSettled: (_data, _err, variables) => {
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
      // Invalidate the affected ticket's detail + history so the detail page
      // and the timeline reflect the new fechaProgramada immediately.
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'detail', variables.ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'history', variables.ticketId] });
    },
  });
}

/**
 * Fetches tickets for a single day.
 * GET /api/v1/plan-matriz/daily/:date
 */
export function useDailyTickets(
  date: string,
  filters: MonthlyFilters = {},
  options?: Omit<UseQueryOptions<DailyViewApiResponse>, 'queryKey' | 'queryFn'>,
) {
  const searchParams = new URLSearchParams();
  appendCalendarFilterParams(searchParams, filters);

  const qs = searchParams.toString();
  const endpoint = `/plan-matriz/daily/${date}${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: monthlyKeys.day(date, filters),
    queryFn: () => apiClient<DailyViewApiResponse>(endpoint),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Mutation to reschedule a ticket to a new date (drag-drop).
 * PUT /api/v1/plan-matriz/reschedule/:ticketId
 *
 * Uses optimistic cache updates for instant visual feedback:
 * - onMutate: removes ticket from source cell immediately
 * - onError: rolls back to previous cache state
 * - onSettled: invalidates query to sync with server
 */
export function useRescheduleTicket(year: number, month: number, filters: MonthlyFilters = {}) {
  const queryClient = useQueryClient();
  const queryKey = monthlyKeys.month(year, month, filters);

  return useMutation({
    mutationFn: ({ ticketId, newDate }: { ticketId: number; newDate: string }) => {
      const body: RescheduleTicketInput = { newDate };
      return apiClient<RescheduleApiResponse>(`/plan-matriz/reschedule/${ticketId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },
    onMutate: async ({ ticketId }) => {
      // Cancel any in-flight refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous cache value for rollback
      const previous = queryClient.getQueryData<MonthlyViewApiResponse>(queryKey);

      if (previous) {
        const next = structuredClone(previous);
        // Remove the ticket from its source date in the cache
        for (const [dateStr, tickets] of Object.entries(next.data.days)) {
          const idx = tickets.findIndex((t) => t.id === ticketId);
          if (idx !== -1) {
            const estado = tickets[idx]?.estadoOt;
            tickets.splice(idx, 1);
            if (tickets.length === 0) {
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- structuredClone already allocated a fresh object tree; rebuilding days{} via spread would add unnecessary allocation for a single-key removal in this optimistic update
              delete next.data.days[dateStr];
            }
            // Update summary counts
            next.data.summary.total -= 1;
            if (estado) {
              const estadoCount = next.data.summary.byStatus[estado];
              if (estadoCount !== undefined && estadoCount > 0) {
                next.data.summary.byStatus[estado] = estadoCount - 1;
              }
            }
            break;
          }
        }
        queryClient.setQueryData(queryKey, next);
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback cache to previous state on error
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      // Always refetch after mutation to ensure server/client consistency
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
